import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router';

import { breadcrumbsFor } from '@/lib/admin-nav';

/**
 * The container every admin page uses. There is no second one.
 *
 * design-system.md §6 asks for exactly this, and it is a fix rather than a
 * convenience: in the prototype the Expenses page is wider than every other
 * admin page, because each page sets its own container. One component means a
 * page cannot drift, and a change to the column applies everywhere at once.
 *
 * Breadcrumbs are derived from the route rather than passed in, so a new page
 * gets them by existing. `record` is the only thing a page has to supply — the
 * name of the thing being looked at, which the router cannot know.
 */
export function AdminPage({
  title,
  description,
  record,
  actions,
  children,
}: {
  title: string;
  description?: ReactNode;
  /** The name of the specific record on a detail page — "Cedar Ridge Rentals". */
  record?: string;
  /** Buttons for the page as a whole. Sits beside the title on wide screens. */
  actions?: ReactNode;
  children?: ReactNode;
}) {
  const { pathname } = useLocation();
  const crumbs = breadcrumbsFor(pathname, record);

  return (
    <div className="mx-auto w-full max-w-content px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
      {crumbs.length > 1 ? (
        <nav aria-label="Breadcrumb" className="mb-3">
          <ol className="flex flex-wrap items-center gap-1 text-meta text-ink-500">
            {crumbs.map((crumb, index) => (
              <li key={`${crumb.label}-${String(index)}`} className="flex items-center gap-1">
                {index > 0 ? (
                  <ChevronRight aria-hidden="true" className="size-3.5 shrink-0" />
                ) : null}
                {crumb.to ? (
                  /*
                   * **44×44, in both directions.** These were 67×17 — a link
                   * somebody taps on a phone with a thumb, and the first admin
                   * screen to be swept at 375px found them (CLAUDE.md §4, P23).
                   * The text stays the size it was; the box around it grows,
                   * which is what "keep the visual size, expand the hit area"
                   * means. `min-w-touch` as well as `min-h-touch`, because a
                   * short crumb like "Jobs" is the case that failed CI once
                   * already on a filter pill.
                   */
                  <Link
                    className="inline-flex min-h-touch min-w-touch items-center justify-center underline-offset-2 hover:text-bark-700 hover:underline"
                    to={crumb.to}
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <span aria-current="page" className="text-ink-700">
                    {crumb.label}
                  </span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      ) : null}

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-h1 text-ink-900">{title}</h1>
          {description ? <p className="mt-1 text-body text-ink-500">{description}</p> : null}
        </div>
        {actions ? <div className="flex shrink-0 flex-wrap gap-2">{actions}</div> : null}
      </div>

      {children}
    </div>
  );
}
